import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { affiliatorGroupApi } from '@/services/subscriptionApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save } from 'lucide-react';
import type {
  AffiliatorGroup,
  AffiliatorGroupOnboardingRequest,
  AffiliatorStatus,
  ProfitShareType,
  CommissionModel,
  PaymentFrequency,
} from '@/types/subscription';

// Zod schema with conditional validation
const affiliatorSchema = z
  .object({
    groupCode: z.string().regex(/^[A-Z0-9_-]+$/, 'Group code must be uppercase alphanumeric with underscores/hyphens').min(3).max(50),
    groupName: z.string().min(1, 'Group name is required').max(100),
    description: z.string().max(500).optional(),
    status: z.enum(['PENDING', 'ACTIVE', 'INACTIVE', 'SUSPENDED', 'TERMINATED', 'REJECTED']),
    contactInformation: z.object({
      contactEmail: z.string().email('Invalid email address'),
      contactPhone: z.string().optional(),
      contactPerson: z.string().min(1, 'Contact person is required'),
      alternateEmail: z.string().email('Invalid email address').optional().or(z.literal('')),
      alternatePhone: z.string().optional(),
      department: z.string().optional(),
      jobTitle: z.string().optional(),
    }),
    companyName: z.string().min(1, 'Company name is required').max(200),
    businessType: z.string().optional(),
    taxId: z.string().optional(),
    businessRegistrationNumber: z.string().optional(),
    businessAddress: z.object({
      street1: z.string().optional(),
      street2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postalCode: z.string().optional(),
      country: z.string().optional(),
    }).optional(),
    profitSharePercentage: z.number().min(0, 'Must be >= 0').max(100, 'Must be <= 100').multipleOf(0.01),
    profitShareType: z.enum(['PERCENTAGE', 'FIXED_AMOUNT']),
    fixedCommissionAmount: z.number().min(0).multipleOf(0.01).optional(),
    commissionModel: z.enum(['ONE_TIME', 'RECURRING']),
    paymentFrequency: z.enum(['MONTHLY', 'QUARTERLY', 'YEARLY']),
    minimumPayoutAmount: z.number().min(0, 'Must be >= 0').multipleOf(0.01),
    preferredCurrency: z.string().regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO code').optional().or(z.literal('')),
    validFrom: z.string().optional(),
    validUntil: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.profitShareType === 'FIXED_AMOUNT') {
        return data.fixedCommissionAmount !== undefined && data.fixedCommissionAmount >= 0;
      }
      return true;
    },
    {
      message: 'Fixed commission amount is required when profit share type is FIXED_AMOUNT',
      path: ['fixedCommissionAmount'],
    }
  );

type AffiliatorFormData = z.infer<typeof affiliatorSchema>;

const STATUSES: AffiliatorStatus[] = ['PENDING', 'ACTIVE', 'INACTIVE', 'SUSPENDED', 'TERMINATED', 'REJECTED'];
const PROFIT_SHARE_TYPES: ProfitShareType[] = ['PERCENTAGE', 'FIXED_AMOUNT'];
const COMMISSION_MODELS: CommissionModel[] = ['ONE_TIME', 'RECURRING'];
const PAYMENT_FREQUENCIES: PaymentFrequency[] = ['MONTHLY', 'QUARTERLY', 'YEARLY'];
const COMMON_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'INR', 'BRL', 'MXN', 'SGD', 'HKD', 'NZD'];

export const AffiliatorGroupFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [autoGenerateCode, setAutoGenerateCode] = useState(false);

  const form = useForm<AffiliatorFormData>({
    resolver: zodResolver(affiliatorSchema),
    defaultValues: {
      groupCode: '',
      groupName: '',
      description: '',
      status: 'PENDING',
      contactInformation: {
        contactEmail: '',
        contactPerson: '',
      },
      companyName: '',
      businessType: '',
      taxId: '',
      businessRegistrationNumber: '',
      businessAddress: {
        street1: '',
        street2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      },
      profitSharePercentage: 0,
      profitShareType: 'PERCENTAGE',
      fixedCommissionAmount: undefined,
      commissionModel: 'RECURRING',
      paymentFrequency: 'MONTHLY',
      minimumPayoutAmount: 100.0,
      preferredCurrency: 'USD',
    },
  });

  const watchedGroupName = form.watch('groupName');
  const watchedProfitShareType = form.watch('profitShareType');

  // Auto-generate group code from group name
  useEffect(() => {
    if (autoGenerateCode && watchedGroupName && !isEdit) {
      const generated = watchedGroupName
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      form.setValue('groupCode', generated);
    }
  }, [watchedGroupName, autoGenerateCode, isEdit, form]);

  // Fetch affiliator group data for edit mode
  const {
    data: _affiliator,
    isLoading: isLoadingAffiliator,
    error: affiliatorError,
  } = useEntity<AffiliatorGroup>({
    fetchFn: (affiliatorId: string) => affiliatorGroupApi.getById(affiliatorId),
    entityId: id,
    entityName: 'affiliatorGroup',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        groupCode: data.groupCode,
        groupName: data.groupName,
        description: data.description || '',
        status: data.status,
        contactInformation: {
          contactEmail: data.contactInformation.contactEmail,
          contactPhone: data.contactInformation.contactPhone || '',
          contactPerson: data.contactInformation.contactPerson,
          alternateEmail: data.contactInformation.alternateEmail || '',
          alternatePhone: data.contactInformation.alternatePhone || '',
          department: data.contactInformation.department || '',
          jobTitle: data.contactInformation.jobTitle || '',
        },
        companyName: data.companyName,
        businessType: data.businessType || '',
        taxId: data.taxId || '',
        businessRegistrationNumber: data.businessRegistrationNumber || '',
        businessAddress: data.businessAddress || {
          street1: '',
          street2: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        },
        profitSharePercentage: data.profitSharePercentage,
        profitShareType: data.profitShareType,
        fixedCommissionAmount: data.fixedCommissionAmount,
        commissionModel: data.commissionModel,
        paymentFrequency: data.paymentFrequency,
        minimumPayoutAmount: data.minimumPayoutAmount,
        preferredCurrency: data.preferredCurrency || 'USD',
        validFrom: data.validFrom,
        validUntil: data.validUntil,
      });
    },
    onError: () => {
      navigate('/admin/affiliators');
    },
  });

  // Create mutation (onboard)
  const createMutation = useMutation({
    mutationFn: async (data: AffiliatorFormData) => {
      const payload: AffiliatorGroupOnboardingRequest = {
        ...data,
        contactInformation: {
          ...data.contactInformation,
          alternateEmail: data.contactInformation.alternateEmail || undefined,
        },
        businessAddress: Object.values(data.businessAddress || {}).some((v) => v) ? data.businessAddress : undefined,
        preferredCurrency: data.preferredCurrency || undefined,
      };
      return await affiliatorGroupApi.onboard(payload);
    },
    entityName: 'affiliatorGroup',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/affiliators');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: AffiliatorFormData) => {
      if (!id) throw new Error('Affiliator Group ID is required');
      const payload: AffiliatorGroupOnboardingRequest = {
        ...data,
        contactInformation: {
          ...data.contactInformation,
          alternateEmail: data.contactInformation.alternateEmail || undefined,
        },
        businessAddress: Object.values(data.businessAddress || {}).some((v) => v) ? data.businessAddress : undefined,
        preferredCurrency: data.preferredCurrency || undefined,
      };
      return await affiliatorGroupApi.update(id, payload);
    },
    entityName: 'affiliatorGroup',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/affiliators');
    },
  });

  const onSubmit = async (data: AffiliatorFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingAffiliator;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading affiliator group..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && affiliatorError) {
    return <ErrorState error={affiliatorError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/affiliators">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Affiliator' : 'Onboard Affiliator'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update affiliator information' : 'Onboard a new affiliator group'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Group identification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                  disabled={isEdit}
                  className="rounded border-gray-300"
                />
                <Label className="text-sm">Auto-generate group code from group name</Label>
              </div>

              <FormField
                control={form.control}
                name="groupCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group Code *</FormLabel>
                    <FormControl>
                      <Input placeholder="GROUP_CODE" {...field} className="font-mono" />
                    </FormControl>
                    <FormDescription>Uppercase alphanumeric with underscores/hyphens (3-50 chars)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="groupName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter group name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter company name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>Primary contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="contactInformation.contactPerson"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Person *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter contact person name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactInformation.contactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="contact@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="contactInformation.contactPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="+1 234 567 8900" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactInformation.alternateEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Alternate Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="alternate@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="contactInformation.department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Department</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter department" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactInformation.jobTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter job title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Business Details */}
          <Card>
            <CardHeader>
              <CardTitle>Business Details</CardTitle>
              <CardDescription>Business information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="businessType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Type</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter business type" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="taxId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tax ID</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter tax ID" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="businessRegistrationNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Registration Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter registration number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <Label>Business Address</Label>
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="businessAddress.street1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street 1</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter street address" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="businessAddress.street2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street 2</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter apartment, suite, etc." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="businessAddress.city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter city" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="businessAddress.state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State/Province</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter state or province" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="businessAddress.postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postal Code</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter postal code" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="businessAddress.country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter country" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Commission Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Commission Configuration</CardTitle>
              <CardDescription>Profit share and commission settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="profitShareType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Profit Share Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PROFIT_SHARE_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {watchedProfitShareType === 'PERCENTAGE' ? (
                  <FormField
                    control={form.control}
                    name="profitSharePercentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Profit Share Percentage *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormDescription>0-100%</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="fixedCommissionAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fixed Commission Amount *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || undefined)}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="commissionModel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Commission Model *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COMMISSION_MODELS.map((model) => (
                            <SelectItem key={model} value={model}>
                              {model.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="paymentFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Frequency *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PAYMENT_FREQUENCIES.map((frequency) => (
                            <SelectItem key={frequency} value={frequency}>
                              {frequency}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="minimumPayoutAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Payout Amount *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="100.00"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="preferredCurrency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Currency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COMMON_CURRENCIES.map((currency) => (
                            <SelectItem key={currency} value={currency}>
                              {currency}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>ISO 3-letter code</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Validity Dates */}
          <Card>
            <CardHeader>
              <CardTitle>Validity Period</CardTitle>
              <CardDescription>Affiliator validity dates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="validFrom"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valid From</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value ? new Date(value).toISOString() : undefined);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="validUntil"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valid Until</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value ? new Date(value).toISOString() : undefined);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/affiliators">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Submit for Approval'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AffiliatorGroupFormPage;
